import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { PageLayout } from '../components/PageLayout';
import { fetchAbout } from '../lib/contentApi';
import { resolveLucideIcon } from '../lib/lucideIcon';
import { Award } from 'lucide-react';

const aboutPhotoSrc = `${process.env.PUBLIC_URL || ''}/photo-about.avif`;

export const About = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAbout()
      .then(setContent)
      .catch((err) => setError(err.message || 'Erro ao carregar conteúdo.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <PageLayout>
        <div className="py-24 text-center text-neutral-600">A carregar…</div>
      </PageLayout>
    );
  }

  if (error || !content) {
    return (
      <PageLayout>
        <div className="py-24 text-center text-red-700">{error || 'Conteúdo indisponível.'}</div>
      </PageLayout>
    );
  }

  const { hero, quemSomos, values, highlights } = content;

  return (
    <PageLayout>
      <section className="relative -mt-20 lg:pt-40 pt-28  pb-16 md:pb-20 bg-[#262d37] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              {hero.title}
            </h1>
            <p className="text-base md:text-lg text-neutral-300 leading-relaxed">
              {hero.subtitle}
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-20 bg-[#e8e6df]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#262d37] mb-4">
                {quemSomos.title}
              </h2>
              <div className="space-y-4 text-base md:text-lg text-[#262d37] leading-relaxed">
                {quemSomos.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="relative h-96 lg:h-full min-h-[400px]">
              <img
                src={aboutPhotoSrc}
                alt="Sobre LuxDall"
                className="w-full h-full object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {highlights.map((item) => {
              const IconComponent = resolveLucideIcon(item.icon, Award);
              return (
                <Card key={item.title} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-[#f2f1ec] border-0">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-[#262d37] rounded-2xl flex items-center justify-center mb-5">
                      <IconComponent size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#262d37] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-[#262d37] leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-20 bg-[#e8e6df]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#262d37] mb-2">
              Os Nossos Valores
            </h2>
            <p className="text-base md:text-lg text-[#262d37] leading-relaxed">
              Princípios que guiam o nosso trabalho
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => {
              const IconComponent = resolveLucideIcon(value.icon, Award);
              return (
                <Card key={value.title} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <IconComponent size={32} className="text-black" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#262d37] mb-2">
                      {value.title}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#262d37] mb-2">
              Áreas de Especialização
            </h2>
            <p className="text-base md:text-lg text-[#262d37] leading-relaxed">
              Soluções personalizadas para cada setor
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Saúde', description: 'Hospitais e clínicas com iluminação especializada' },
              { title: 'Educação', description: 'Escolas e universidades com ambientes otimizados' },
              { title: 'Institucional', description: 'Edifícios públicos e centros culturais' },
              { title: 'Comercial', description: 'Espaços comerciais e corporativos' },
            ].map((area) => (
              <Card key={area.title} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-[#f2f1ec] border-0">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-[#262d37] mb-2">{area.title}</h3>
                  <p className="text-[#262d37] leading-relaxed">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
